import { clamp, debounce, dropRight, forEach, head, includes, last, memoize, parseInt, uniq } from 'lodash/fp';
import { Field, P, Slider } from '@ahmdigital/ui';
import { PureComponent } from 'react';
import getIncomeTypeFromScale from '@ahmdigital/logic/lib/utils/get-income-type-from-scale';

import { Customer } from '../../../../../selectors/select-customer/types';
import { Rate } from '../../../../../types/__codegen__/sanity/graphql';
import { StateValues } from '../../../../../actions/customer';
import calculateMls from '../../utils/calculate-mls';
import constants from '../../../../../ahm-constants';
import elementAnalytics from '../../../../../analytics/element';
import getScaleOptions from '../../../../../utils/get-scale-options';
import getThresholdsFromTaxRate from '../calculator-section/utils/get-thresholds-from-tax-rate';
import MlsSliderTip from './components/mls-slider-tip';
import styles from './styles.module.sass';

const amountToDrop = 1;
const radix = 10;

const DROPDOWN_PROPS = {
  type: 'dropdown',
};

const scaleOptions = getScaleOptions();

const bindActionToEvent =
  // @ts-expect-error - Automatic, Please fix when editing this file


    (actionFunction) =>
    // @ts-expect-error - Automatic, Please fix when editing this file
    ({ target: { value } }) => {
      actionFunction(value);
    };

// @ts-expect-error - Automatic, Please fix when editing this file
const analyticsFnGenerator = (label, fn, type) =>
  elementAnalytics(
    {
      analytics: {
        component: 'TaxCalculator',
        label,
      },
    },
    type === 'event' ? bindActionToEvent(fn) : fn,
  );

const tipFormatterGenerator =
  // @ts-expect-error - Automatic, Please fix when editing this file


    ({ limits, scale }) =>
    // @ts-expect-error - Automatic, Please fix when editing this file
    (value) => <MlsSliderTip limits={limits} scale={scale} value={value} />;

export const calculateSliderProps = memoize((scale, activeTaxRate) => {
  const incomeType = getIncomeTypeFromScale(scale);
  const isSingleIncome = incomeType === constants.INCOME_TYPE.SINGLE;
  const thresholds = getThresholdsFromTaxRate(activeTaxRate, incomeType);

  const marks = {};
  forEach(
    (threshold) => {
      // @ts-expect-error - Automatic, Please fix when editing this file
      const mark = threshold.max / 1000;
      // @ts-expect-error - Automatic, Please fix when editing this file
      marks[mark] = `${mark.toString()}k`;
    },
    dropRight(amountToDrop, thresholds),
  );

  // @ts-expect-error - Automatic, Please fix when editing this file
  const firstMark = head(thresholds).max / 1000;
  // @ts-expect-error - Automatic, Please fix when editing this file
  const lastMark = last(thresholds).min / 1000;

  const min = firstMark - (isSingleIncome ? 15 : 30);
  const max = lastMark + (isSingleIncome ? 15 : 30);

  return { firstMark, lastMark, marks, max, min, thresholds };
});

export type CalculatorChangeValues = {
  income: number;
  mlsAmount: number;
  scale: Customer['scale'];
  state: Customer['state'];
};
export type CalculatorOnChange = (values: CalculatorChangeValues) => void;
export type CalculatorOnIncomeChange = (income: number) => void;
export type CalculatorOnScaleChange = (scale: Customer['scale']) => void;
export type CalculatorOnStateChange = (state: StateValues) => void;

type CalculatorProps = {
  activeTaxRate: Rate;
  customer: Customer;
  onChange: CalculatorOnChange;
  onIncomeChange: CalculatorOnIncomeChange;
  onScaleChange: CalculatorOnScaleChange;
  onStateChange: CalculatorOnStateChange;
};

class Calculator extends PureComponent<CalculatorProps> {
  // @ts-expect-error - Automatic, Please fix when editing this file
  componentDidMount() {
    // We throttle redux action/reducer/render to improve front end performance.
    // This assumes income is not needed for in-page rendering,
    // but only for maintaining customer state data and analytics purposes.
    const { onIncomeChange, onScaleChange, onStateChange, customer } = this.props;
    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onIncomeChange = debounce(500, (value) => onIncomeChange(value));
    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onScaleChange = analyticsFnGenerator('scale', onScaleChange, 'event');
    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onStateChange = analyticsFnGenerator('state', onStateChange, 'event');

    if (onIncomeChange) {
      onIncomeChange(customer?.income ?? 0);
    }

    if (customer?.scale && onScaleChange) {
      onScaleChange(customer.scale);
    }
  }

  // @ts-expect-error - Automatic, Please fix when editing this file
  componentDidUpdate(prevProps) {
    const { customer: prevCustomer } = prevProps;
    const { activeTaxRate, customer } = this.props;
    const incomeTypes = [customer.incomeType, prevCustomer.incomeType];
    if (uniq(incomeTypes).length === 2 && includes(constants.INCOME_TYPE.SINGLE, incomeTypes)) {
      const marks = calculateSliderProps(customer.scale, activeTaxRate);
      // @ts-expect-error - Automatic, Please fix when editing this file
      this.onSliderChange(parseInt(radix, marks.min));
    }
  }

  // @ts-expect-error - Automatic, Please fix when editing this file
  onSliderChange = (value) => {
    const { activeTaxRate, customer, onChange } = this.props;

    const { firstMark, lastMark, thresholds } = calculateSliderProps(customer.scale, activeTaxRate);
    // We clamp income for the mls calculation because we want a nice round number for the calculator results,
    // but we use the unclamped income for the income tier calculation because we want it work for rebate tier 3 (which starts at $140,001).
    const income = value * 1000;
    const clampedIncome = clamp(firstMark * 1000, lastMark * 1000, income);

    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onIncomeChange(income);

    const mlsAmount = calculateMls({
      income: clampedIncome,
      thresholds,
    });
    onChange({
      income,
      mlsAmount,
      scale: customer.scale,
      state: customer.state,
    });
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  render() {
    const { activeTaxRate, customer } = this.props;
    const { firstMark, lastMark, marks, max, min } = calculateSliderProps(customer.scale, activeTaxRate);
    const isSingleIncome = customer.incomeType === constants.INCOME_TYPE.SINGLE;

    return (
      <div className={styles.calculator}>
        <div className={styles.fieldsWrapper}>
          <P looksLike="h4" variant="regular">
            If you’re a
          </P>
          <div className={styles.field}>
            <Field
              {...DROPDOWN_PROPS}
              size="auto"
              ariaLabel="scale"
              name="scale"
              // @ts-expect-error - Automatic, Please fix when editing this file
              onChange={this.onScaleChange}
              options={scaleOptions}
              value={customer.scale}
            />
          </div>
          <P looksLike="h4" variant="regular">
            in
          </P>
          <div className={styles.field}>
            <Field
              {...DROPDOWN_PROPS}
              size="auto"
              ariaLabel="state"
              name="state"
              // @ts-expect-error - Automatic, Please fix when editing this file
              onChange={this.onStateChange}
              options={constants.STATE_OPTIONS}
              value={customer.state}
            />
          </div>
          <P looksLike="h4" variant="regular">
            and earning
          </P>
        </div>
        <div className={styles.sliderContainer}>
          <Slider
            key={isSingleIncome ? 'slider-single' : 'slider-couple'}
            analytics={{ action: 'on_tax_income_move_slider', component: 'tax' }}
            {...{ marks, max, min }}
            name="income"
            onChange={this.onSliderChange}
            tipFormatter={tipFormatterGenerator({
              limits: { max: Math.floor(lastMark), min: Math.floor(firstMark) },
              scale: customer.scale,
            })}
            ariaLabelForHandle="income"
            ariaValueTextFormatterForHandle={() => customer?.income?.toString() ?? ''}
          />
        </div>
      </div>
    );
  }
}

export default Calculator;
